import React, { SFC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Button,
    Dialog,
    DialogTitle,
    makeStyles,
    DialogContent,
    DialogActions,
    Grid,
    Typography,
    TextField as MaterialTextField,
    Checkbox,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    IconButton,
} from '@material-ui/core'
import { Form } from 'react-final-form'
import { TextField as RffTextField } from 'mui-rff'
import { UserModel, UserRfid } from '../../../shared/types/user'
import { ValidationErrors } from 'final-form'
import { useDispatch, useSelector } from 'react-redux'
import { addUser, changeUserPassword, editUser } from '../../../redux/actions/users'
import { setRequiredErrors } from '../../../utils/ui'
import { validateEmail } from '../../../shared/utils/validation'
import { DialogProgress } from '../../../shared/components/dialogProgress'
import { DialogConfirmButton } from '../../../shared/components/dialogConfirmButton'
import { getJson, sendJson } from '../../../shared/utils/api'
import { UserUnpaidSummary } from '../../../shared/types/user'
import { CountrySelect } from '../../../shared/components/countrySelect'
import { TagModel, TagUser } from '../../../shared/types/tag'
import { Autocomplete } from '@material-ui/lab'
import { config } from '../../../config'
import { Tenant } from '../../../shared/types/tenant'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiFilledInput-root': {
            borderRadius: theme.spacing(0.5),
        },
        '& .MuiFilledInput-root::before': {
            border: 0,
        },
    },
    dialogActions: {
        padding: theme.spacing(1, 3, 3, 3),
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflowY: 'hidden',
    },
    rfidinput: {
        width: '235px',
    },
    rfidbutton: {
        position: 'relative',
        top: '10px',
        float: 'right',
    },
    accordionSummary: {
        height: 57,
        backgroundColor: "rgba(0, 0, 0, 0.09)",
        border: "1px solid transparent",
    },
    accordionLabel: {
        color: "inherit",
    },
}))

interface UserAddEditDialogProps {
    isAdd: boolean
    onClose(user?: UserModel): void
    initialModel?: UserModel
}
const UserAddEditDialog: SFC<UserAddEditDialogProps> = ({ isAdd, onClose, initialModel }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const dispatch = useDispatch()
    const stateSelector = isAdd ? (state: any) => state.users.add : (state: any) => state.users.edit
    const [showHidden, setShowHidden] = useState<boolean>(false)
    const [confirmed, setConfirmed] = useState<boolean>(isAdd ? true : (initialModel?.confirmed ?? false))
    const [blocked, setBlocked] = useState<boolean>(initialModel?.blocked ?? false)
    
    const [monthlyAllowance, setMonthlyAllowance] = useState<number>(initialModel?.monthlyAllowance ?? 0)

    // zrusenie / znizenie mesacneho limitu: ak ma pouzivatel nevyuctovany priebezny mesiac nad novym limitom,
    // admin sa rozhodne, ci ho vyuctovat hned, alebo nechat na mesacnu fakturaciu (1. v mesiaci)
    const [billPrompt, setBillPrompt] = useState<{ amount: number; result: any } | null>(null)
    const [billInProgress, setBillInProgress] = useState<boolean>(false)
    const [billMessage, setBillMessage] = useState<{ text: string; error: boolean } | null>(null)
    const hasPaymentCard = Boolean(initialModel?.paymentRequestId)

    const getUnbilledMonthAmountAboveLimit = async (newAllowance: number): Promise<number> => {
        const initialAllowance = initialModel?.monthlyAllowance ?? 0
        if (isAdd || !initialModel?.id || initialAllowance <= 0 || newAllowance >= initialAllowance) {
            return 0
        }
        try {
            const summary: UserUnpaidSummary = await getJson(`users/${initialModel.id}/unpaidSummary`)
            const accrual = summary?.currentMonthUnpaid ?? 0
            // limit zruseny -> akakolvek suma; limit zniezeny -> len ak uz minuta suma presahuje novy limit
            const exceeds = newAllowance <= 0 ? accrual > 0 : accrual > newAllowance
            return exceeds ? accrual : 0
        } catch {
            return 0
        }
    }

    const billCurrentMonthNow = async () => {
        if (!initialModel?.id) return
        setBillInProgress(true)
        setBillMessage(null)
        try {
            await sendJson(`users/${initialModel.id}/payDebts?includeCurrentMonth=true`, 'POST')
            setBillMessage({ text: t('users.billMonthSuccess'), error: false })
        } catch (error: any) {
            // kody z backendu (DebtPaymentResultType): 1 = platba zlyhala, 2 = bez karty
            const code = error?.errors?.[0]?.code
            // eslint-disable-next-line eqeqeq
            const text = code == 2 ? t('users.noPaymentCard') : code == 1 ? t('users.collectDebtFailed') : t('users.paymentActionError')
            setBillMessage({ text, error: true })
        } finally {
            setBillInProgress(false)
        }
    }

    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const isSuperAdmin = tenantId === config.mainTenantId
    const tenantsState = useSelector((state: any) => state.tenants)
    const [userTenants, setUserTenants] = useState<string>(initialModel?.tenants || '')
    const [allTenants, setAllTenants] = useState<Tenant[]>([])

    const [rfids, setRfids] = useState<UserRfid[]>(initialModel?.rfids || [])
    // State to manage the input value
    const [inputValue, setInputValue] = useState<string>('')

    const [tenantsMapped, setTenantsMapped] = useState<Tenant[]>([])

    const mainTenantId = config.mainTenantId

    const allTags = useSelector((state: any) => state.tags.items.user.value)
    const [userTags, setUserTags] = useState<TagUser[]>(initialModel?.tags || [])
    const [userTagsMapped, setUserTagsMapped] = useState<TagModel[]>([])

    const handleShowHiddenChange = () => {
        setShowHidden(!showHidden)
    }

    const handleConfirmedChange = () => {
        setConfirmed(!confirmed)
    }

    const handleBlockedChange = () => {
        setBlocked(!blocked)
    }

    const generatePassword = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*'
        let password = ''

        for (let i = 0; i < 12; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length))
        }

        return password
    }

    const handleTagChange = (event: any, newValue: TagModel[]) => {
        setUserTagsMapped(newValue)

        const newUserTags: TagUser[] = newValue.map((tag) => ({
            tagId: tag.id,
            userId: initialModel?.id,
        }))

        setUserTags(newUserTags)
    }
    const handleTenantChange = (event: any, newValue: Tenant[]) => {
        setTenantsMapped(newValue)
        setUserTenants(newValue.map((tenant) => tenant.id).join(','))
    }

    const handleAddRfid = () => {
        if (inputValue.trim()) {
            const newRfid: UserRfid = { rfid: inputValue, userId: initialModel?.id }
            setRfids((prevRfids) => [...prevRfids, newRfid])
            setInputValue('')
        }
    }

    const handleRemoveRfid = (rfidToRemove: string) => {
        setRfids((prevRfids) => prevRfids.filter((rfid) => rfid.rfid !== rfidToRemove))
    }

    const setTenants = () => {
        if (tenantId !== mainTenantId) {
            if (isAdd) {
                return tenantId
            } else {
                // check if it is not already included
                if (initialModel && !initialModel?.tenants.includes(tenantId)) {
                    return (initialModel.tenants += ',' + tenantId)
                }
            }
        } else {
            if (isAdd && userTenants === '') {
                return config.chargeTenantId
            } else {
                return userTenants
            }
        }
    }

    useEffect(() => {
        if (initialModel && initialModel.tags && allTags) {
            // Filter allTags to select only those TagModel objects whose id matches TagCharger.tagId
            const selectedTags = allTags.filter((tag: TagModel) =>
                initialModel.tags?.some((tagUser: TagUser) => tag.id === tagUser.tagId)
            )
            setUserTagsMapped(selectedTags)
        }
    }, [initialModel, allTags])

    useEffect(() => {
        if (initialModel && initialModel.tenants && allTenants) {
            const userTenants = allTenants.filter((tenant: Tenant) => initialModel.tenants.includes(tenant.id))
            setTenantsMapped(userTenants)
        }
    }, [initialModel, allTenants])

    useEffect(() => {
        if (tenantsState && tenantsState.value) {
            const allTenants = tenantsState.value.filter((tenant: Tenant) => tenant.id !== mainTenantId)
            setAllTenants(allTenants)
        }
    }, [initialModel, tenantsState, mainTenantId])

    useEffect(() => {
        if (initialModel?.showHiddenLocation) {
            setShowHidden(initialModel.showHiddenLocation)
        }
    }, [initialModel])

    const formInitialValues = {
        ...initialModel,
        password: isAdd ? initialModel?.password : '',
    }

    return (
        <Dialog className={classes.dialog} open={true}>
            <DialogProgress stateSelector={stateSelector} />
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{isAdd ? t('users.newUser') : t('users.editUser')}</span>
                    {!isAdd && (
                        <Typography variant="caption" style={{ display: 'flex', alignItems: 'center', color: '#000000', fontSize: '15px' }}>
                            <Checkbox
                                name="blocked"
                                checked={blocked}
                                disabled={!isSuperAdmin}
                                onChange={handleBlockedChange}
                                style={{
                                    color: isSuperAdmin ? '#6100ED' : undefined,
                                    padding: '4px',
                                }}
                            />
                            {t('users.blocked')}
                            <Tooltip title={t('users.blockedHelp')} arrow>
                                <span style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'pointer' }}>
                                    <IconButton
                                        size="small"
                                        style={{
                                            marginLeft: 4,
                                            pointerEvents: 'auto',
                                        }}
                                    >
                                        <HelpOutlineIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Typography>
                    )}
                </div>
            </DialogTitle>

            <Form
                onSubmit={async (model) => {
                    const newUserObj = {
                        ...model,
                        companyName: model.companyName ?? '',
                        companyId: model.companyId ?? '',
                        taxId: model.taxId ?? '',
                        vatId: model.vatId ?? '',
                        phone: model.phone ?? '',
                        monthlyAllowance: monthlyAllowance,
                        showHiddenLocation: showHidden,
                        confirmed: confirmed,
                        blocked: blocked,
                        tags: userTags,
                        tenants: setTenants(),
                        rfids,
                        cars: null,
                    }

                    const result: any = await dispatch(
                        isAdd ? addUser(newUserObj as UserModel) : editUser(newUserObj as UserModel)
                    )
                    if (result) {
                        if (!isAdd && model.password?.trim() && initialModel?.id) {
                            await dispatch(changeUserPassword(initialModel.id, model.password.trim()))
                        }

                        // vyuctovanie z adminu moze len superadmin (backend to kontroluje tiez)
                        const unbilled = isSuperAdmin ? await getUnbilledMonthAmountAboveLimit(monthlyAllowance) : 0
                        if (unbilled > 0) {
                            setBillPrompt({ amount: unbilled, result })
                            return
                        }

                        onClose(result)
                    }
                }}
                initialValues={formInitialValues}
                validate={(model: any): ValidationErrors => {
                    const result: any = {}

                    let requiredFields = ['firstName', 'lastName', 'email']
                    if (isAdd) {
                        requiredFields = [...requiredFields, 'password']
                    }
                    if (!isAdd && initialModel?.paymentRequestId) {
                        requiredFields = [...requiredFields, 'street', 'zip', 'city', 'country']
                    }
                    setRequiredErrors(result, model, requiredFields, t('common.required'))
                    if (model.email && !validateEmail(model.email)) {
                        result.email = t('users.invalidEmail')
                    }
                    return result
                }}
                render={({ handleSubmit, form }) => (
                    <form onSubmit={handleSubmit} noValidate className={classes.form}>
                        <DialogContent>
                            <Grid container alignItems="flex-start" spacing={2}>
                                <Grid item xs={6}>
                                    <RffTextField
                                        name="firstName"
                                        fullWidth
                                        required
                                        inputProps={{
                                            maxLength: 64,
                                        }}
                                        type="text"
                                        variant="filled"
                                        label={t('users.firstName')}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <RffTextField
                                        name="lastName"
                                        fullWidth
                                        required
                                        inputProps={{
                                            maxLength: 64,
                                        }}
                                        type="text"
                                        variant="filled"
                                        label={t('users.lastName')}
                                    />
                                </Grid>
                                <Grid item xs={8}>
                                    <RffTextField
                                        name="email"
                                        fullWidth
                                        required
                                        inputProps={{
                                            maxLength: 320,
                                        }}
                                        type="email"
                                        variant="filled"
                                        label={t('users.email')}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <RffTextField
                                        name="phone"
                                        fullWidth
                                        inputProps={{
                                            maxLength: 32,
                                        }}
                                        type="text"
                                        variant="filled"
                                        label={t('users.phone')}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item xs={9}>
                                            <RffTextField
                                                name="password"
                                                fullWidth
                                                required={isAdd}
                                                inputProps={{
                                                    maxLength: 64,
                                                }}
                                                type="text"
                                                variant="filled"
                                                label={isAdd ? t('users.password') : t('common.newPassword')}
                                            />
                                        </Grid>

                                        <Grid item xs={3}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => {
                                                    form.change('password', generatePassword())
                                                }}
                                            >
                                                {t('common.generate')}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Accordion>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="address-content"
                                            className={classes.accordionSummary}
                                        >
                                            <Typography variant="subtitle1" className={classes.accordionLabel}>
                                                {t("common.address")}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={9}>
                                                    <RffTextField
                                                        name="street"
                                                        fullWidth
                                                        required={
                                                            initialModel?.paymentRequestId !== null &&
                                                            initialModel?.paymentRequestId !== undefined
                                                        }
                                                        inputProps={{
                                                            maxLength: 256,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.street')}
                                                    />
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <RffTextField
                                                        name="zip"
                                                        fullWidth
                                                        required={
                                                            initialModel?.paymentRequestId !== null &&
                                                            initialModel?.paymentRequestId !== undefined
                                                        }
                                                        inputProps={{
                                                            maxLength: 64,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.zip')}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <RffTextField
                                                        name="city"
                                                        fullWidth
                                                        required={
                                                            initialModel?.paymentRequestId !== null &&
                                                            initialModel?.paymentRequestId !== undefined
                                                        }
                                                        inputProps={{
                                                            maxLength: 256,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.city')}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <CountrySelect
                                                        name="country"
                                                        required={
                                                            initialModel?.paymentRequestId !== null &&
                                                            initialModel?.paymentRequestId !== undefined
                                                        }
                                                        label={t('users.state')}
                                                        variant="filled"
                                                    ></CountrySelect>
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                <Grid item xs={12}>
                                    <Accordion>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="address-content"
                                            className={classes.accordionSummary}
                                        >
                                            <Typography variant="subtitle1" className={classes.accordionLabel}>
                                                {t("users.companyInformation")}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <RffTextField
                                                        name="companyName"
                                                        fullWidth
                                                        inputProps={{
                                                            maxLength: 64,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.companyName')}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <RffTextField
                                                        name="companyId"
                                                        fullWidth
                                                        inputProps={{
                                                            maxLength: 64,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.companyId')}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <RffTextField
                                                        name="taxId"
                                                        fullWidth
                                                        inputProps={{
                                                            maxLength: 64,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.taxId')}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <RffTextField
                                                        name="vatId"
                                                        fullWidth
                                                        inputProps={{
                                                            maxLength: 64,
                                                        }}
                                                        type="text"
                                                        variant="filled"
                                                        label={t('users.vatId')}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={allTags}
                                        getOptionLabel={(tag: TagModel) => tag.name}
                                        value={userTagsMapped}
                                        onChange={handleTagChange}
                                        // 1. Úprava zobrazenia už vybratých tagov (Chips)
                                        renderTags={(value: TagModel[], getTagProps) =>
                                            value.map((option: TagModel, index: number) => {
                                                const isExclusive = option.isExclusive; // Prispôsob presný názov vlastnosti (napr. IsExclusive)
                                                return (
                                                    <Chip
                                                        key={option.id}
                                                        label={option.name}
                                                        {...getTagProps({ index })}
                                                        style={{
                                                            margin: 4,
                                                            backgroundColor: isExclusive ? '#f1eaff' : undefined, // Jemné červené pozadie
                                                            color: isExclusive ? '#6100ED' : undefined,          // Tmavočervený text
                                                            border: isExclusive ? '1px solid #DBC4FF' : undefined,
                                                            fontWeight: isExclusive ? 'bold' : 'normal'
                                                        }}
                                                    />
                                                );
                                            })
                                        }
                                        // 2. Úprava zobrazenia možností v rozbaľovacom zozname (Dropdown)
                                        renderOption={(option: TagModel) => (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                <span style={{ fontWeight: option.isExclusive ? 'bold' : 'normal' }}>
                                                    {option.name}
                                                </span>
                                                {option.isExclusive && (
                                                    <span style={{ fontSize: '11px', color: '#6100ED', backgroundColor: '#f1eaff', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                        EXCLUSIVE
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        renderInput={(params) => (
                                            <MaterialTextField
                                                {...params}
                                                variant="filled"
                                                label="Tags"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={7}>
                                    <MaterialTextField
                                        className={classes.rfidinput}
                                        variant="filled"
                                        label="RFID"
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value.trim())}
                                    />
                                    <Button
                                        className={classes.rfidbutton}
                                        variant="contained"
                                        color="primary"
                                        onClick={handleAddRfid}
                                        disabled={!inputValue.trim()}
                                    >
                                        {t('users.addToList')}
                                    </Button>
                                </Grid>
                                <Grid item xs={5} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <MaterialTextField
                                        fullWidth
                                        type="number"
                                        variant="filled"
                                        inputProps={{ min: 1 }}
                                        label={t('users.monthlyAllowance')}
                                        value={monthlyAllowance}
                                        onChange={(e) => setMonthlyAllowance(Math.round(Number(e.target.value)))}
                                    />
                                    <Tooltip title='Monthly allowance in euros. Whole number only, no decimals. Leave empty or set to 0 for no limit.' arrow>
                                        <span style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'pointer' }}>
                                            <IconButton
                                                size="small"
                                                style={{
                                                    marginLeft: 4,
                                                    bottom: 1,
                                                    pointerEvents: 'auto',
                                                }}
                                            >
                                                <HelpOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    {rfids.map((rfid) => (
                                        <Chip
                                            key={rfid.id}
                                            label={rfid.rfid}
                                            onDelete={() => handleRemoveRfid(rfid.rfid)}
                                            style={{ margin: 4 }}
                                        />
                                    ))}
                                </Grid>
                                <Grid hidden={!(tenantId && tenantId === mainTenantId)} item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={allTenants}
                                        getOptionLabel={(tenant: Tenant) => tenant.name}
                                        value={tenantsMapped}
                                        onChange={handleTenantChange}
                                        renderInput={(params) => (
                                            <MaterialTextField
                                                {...params}
                                                variant="filled"
                                                label="Tenants"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" style={{ fontSize: '18px', color: '#000000' }}>
                                        <Checkbox
                                            name="showHiddenLocation"
                                            checked={showHidden}
                                            onChange={handleShowHiddenChange}
                                            style={{
                                                color: '#6100ED',
                                                marginBottom: '4px',
                                                paddingLeft: '0px',
                                                paddingBottom: '0px',
                                                paddingTop: '0px',
                                                paddingRight: '8px',
                                            }}
                                        />
                                        {t('users.showHiddenLocation')}
                                    </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="caption" style={{ fontSize: '18px', color: '#000000' }}>
                                        <Checkbox
                                            name="confirmed"
                                            checked={confirmed}
                                            onChange={handleConfirmedChange}
                                            style={{
                                                color: '#6100ED',
                                                marginBottom: '4px',
                                                paddingLeft: '0px',
                                                paddingBottom: '0px',
                                                paddingTop: '0px',
                                                paddingRight: '8px',
                                            }}
                                        />
                                        {t('users.confirmed')}
                                    </Typography>
                                </Grid>

                                {/* <Grid item xs={6}>
                                    <Typography variant="caption" style={{ fontSize: '18px', color: '#000000' }}>
                                        <Checkbox
                                            checked={monthlyAllowanceEnabled}
                                            onChange={() => setMonthlyAllowanceEnabled(!monthlyAllowanceEnabled)}
                                            style={{
                                                color: '#6100ED',
                                                marginBottom: '4px',
                                                paddingLeft: '0px',
                                                paddingBottom: '0px',
                                                paddingTop: '0px',
                                                paddingRight: '8px',
                                            }}
                                        />
                                        {t('users.monthlyAllowance')}
                                    </Typography>
                                </Grid>
                                <>
                                    {monthlyAllowanceEnabled && (
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                variant="filled"
                                                inputProps={{ min: 1 }}
                                                label={t('users.monthlyAllowanceHint')}
                                                value={monthlyAllowance}
                                                onChange={(e) =>
                                                    setMonthlyAllowance(Math.round(Number(e.target.value)))
                                                }
                                            />
                                        </Grid>
                                    )}
                                </> */}
                            </Grid>
                        </DialogContent>
                        <DialogActions className={classes.dialogActions}>
                            <Button
                                onClick={() => {
                                    onClose()
                                }}
                                color="primary"
                            >
                                {t('common.cancel')}
                            </Button>
                            <DialogConfirmButton
                                label={isAdd ? t('common.create') : t('common.change')}
                                stateSelector={stateSelector}
                            />
                        </DialogActions>
                    </form>
                )}
            />

            {billPrompt && (
                <Dialog open={true} maxWidth="sm">
                    <DialogTitle>{t('users.billMonthTitle')}</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">
                            {!hasPaymentCard
                                ? t('users.billMonthContentInfo', { amount: billPrompt.amount.toFixed(2) })
                                : monthlyAllowance > 0
                                    ? t('users.billMonthContentLowered', { amount: billPrompt.amount.toFixed(2), limit: monthlyAllowance })
                                    : t('users.billMonthContentRemoved', { amount: billPrompt.amount.toFixed(2) })}
                        </Typography>
                        {!hasPaymentCard && (
                            <Typography variant="body2" style={{ marginTop: 12, color: '#c62828' }}>
                                {t('users.billMonthNoCard')}
                            </Typography>
                        )}
                        {billMessage && (
                            <Typography
                                variant="body2"
                                style={{ marginTop: 12, color: billMessage.error ? '#c62828' : '#2e7d32' }}
                            >
                                {billMessage.text}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        {billMessage || !hasPaymentCard ? (
                            <Button color="primary" onClick={() => onClose(billPrompt.result)}>
                                {t('common.close')}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    color="primary"
                                    disabled={billInProgress}
                                    onClick={() => onClose(billPrompt.result)}
                                >
                                    {t('users.billMonthLater')}
                                </Button>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    disabled={billInProgress}
                                    onClick={billCurrentMonthNow}
                                >
                                    {t('users.billMonthNow', { amount: billPrompt.amount.toFixed(2) })}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </Dialog>
            )}
        </Dialog>
    )
}

export default UserAddEditDialog
